import { useEffect, useRef } from 'react';
import { Control, UseFormGetValues, useWatch } from 'react-hook-form';
import {
    ITableForm,
    TableFormValues,
} from '../../../common/interfaces/table.interface';
import { RootState, useAppDispatch, useAppSelector } from '../../base-redux';
import { tablesActions, tablesSelectors } from '../../../actions';
import { FilterTypes } from '../table.consts';
import { useTheme } from '../../base-components';
import { getBasicColorsObject } from '../../../common/utils/base.utils';
import { IActiveFilter } from '../table.interfaces';
const useFiltersAndSortUpdate = (
    control: Control<ITableForm>,
    getValues: UseFormGetValues<ITableForm>
) => {
    const dispatch = useAppDispatch();
    const isFirstRender = useRef(true);
    const theme = useTheme();
    const baseColors = getBasicColorsObject(theme.palette);

    // Watch the 'filters' and 'sort' fields
    const filters = useWatch({
        control,
        name: TableFormValues.filters,
    });

    const sort = useWatch({
        control,
        name: TableFormValues.sort,
    });

    const tableState = useAppSelector((state: RootState) =>
        tablesSelectors.getTablesState(
            state,
            getValues(TableFormValues.tableId)
        )
    );
    const shouldFilterData = (formData: ITableForm) => {
        return formData.filters.every((filter: IActiveFilter) => {
            const { values, type } = filter;
            let returnAssertion = false;
            switch (type) {
                case FilterTypes.options:
                case FilterTypes.tags:
                    returnAssertion = filter.values.length > 0;
                    break;
                case FilterTypes.datepicker_range:
                    returnAssertion = values[0] !== null || values[1] !== null;
                    break;
                case FilterTypes.score_range:
                case FilterTypes.number_range:
                    returnAssertion = values[0] !== null || values[1] !== null;
                    break;
                case FilterTypes.boolean:
                    returnAssertion = filter.booleanValue !== undefined;
                    break;
                default:
                    break;
            }
            return returnAssertion;
        });
    };

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false; // Set to false after first render
            return; // Skip the effect
        }
        const formData = getValues();
        if (!tableState.initialLoad) {
            dispatch(
                tablesActions.fetchTableDataById({
                    tableId: formData.tableId,
                    formData,
                    page: 1,
                    baseColors,
                })
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch, getValues, sort]);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        const formData = getValues();
        if (shouldFilterData(formData)) {
            dispatch(
                tablesActions.fetchTableDataById({
                    tableId: formData.tableId,
                    formData: formData,
                    page: 1,
                    baseColors,
                })
            );
        }
    }, [filters, dispatch, getValues]);
};

export default useFiltersAndSortUpdate;
